import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
} from "@mui/material";
import { CheckCircle, Close as CloseIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setIsShowSetupMenu, setShowPaymentSuccessDialog, switchProfile } from "src/redux/slices/AuthSlice";
import NameLogo from "./icons/Name-Logo";
import EmailVerificationDialog from "./EmailVerificationDialog";
import OtpVerificationDialog from "./OtpVerificationDialog";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import toast from "react-hot-toast";

const PaymentSuccessDialog = () => {
  const dispatch = useDispatch();
  const showPaymentSuccessDialog = useSelector(
    (state) => state.auth.showPaymentSuccessDialog
  );
  const user = useSelector((state) => state.auth.user);
  const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);
  const lastSubscriptionBusinessId = useSelector((state) => state.auth.lastSubscriptionBusinessId);
  
  const [openEmailVerify, setOpenEmailVerify] = useState(false);
  const [openOtpVerify, setOpenOtpVerify] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const businessId = selectedBusinessId || lastSubscriptionBusinessId;
  
  const business = useMemo(() => 
    user?.business?.find((b) => b._id === businessId),
    [user?.business, businessId]
  );
  
  const businessEmail = business?.businessEmail || "";

  const openSetupMenu = () => {
    if (!selectedBusinessId && businessId && business) {
      dispatch(switchProfile({
        type: "business",
        businessId: businessId,
        primaryLocationId: business.primaryLocationId,
      }));
    }
    dispatch(setShowPaymentSuccessDialog(false));
    dispatch(setIsShowSetupMenu(true));
  };

  const handleClose = () => {
    dispatch(setShowPaymentSuccessDialog(false));
  };

  const handleSetupAccount = () => {
    if (!business?.isEmailVerified) {
      setOpenEmailVerify(true);
    } else {
      openSetupMenu();
    }
  };

  const handleSendOtp = async () => {
    if (!businessId) return;
    setIsSendingOtp(true);
    try {
      const response = await axiosInstance.get(
        `${ApiEndPoints.BUSINESS.resendOtp}/${businessId}`
      );
      if (response.data.status === "success") {
        toast.success("otp has been sent to your business email!");
        setOpenEmailVerify(false);
        setOpenOtpVerify(true);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "failed to send otp. please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpVerify = async (otpString) => {
    if (!businessId) return;
    try {
      const response = await axiosInstance.post(`${ApiEndPoints.BUSINESS.verifyOtp}/${businessId}`, {
        otp: otpString,
      });
      if (response.data.status === "success") {
        toast.success("email has been verified!");
        setOpenOtpVerify(false);
        openSetupMenu();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "failed to verify otp. please try again.");
    }
  };

  const handleEmailDialogClose = () => {
    setOpenEmailVerify(false);
    openSetupMenu();
  };

  const handleOtpDialogClose = () => {
    setOpenOtpVerify(false);
    openSetupMenu();
  };

  return (
    <>
    <Dialog
      open={showPaymentSuccessDialog && !openEmailVerify}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1500,
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: "relative" }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            p: 4,
            pt: 5,
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 2 }}>
            <Avatar
              src="/logo.svg"
              sx={{
                width: 90,
                height: 90,
                bgcolor: "#fff",
                border: "4px solid #A259FF",
                mx: "auto",
                padding: 5,
              }}
            >
              <NameLogo />
            </Avatar>
          </Box>

          {/* Brand Name */}
          <Typography
            variant="h3"
            align="center"
            fontWeight="bold"
            letterSpacing={1}
            sx={{
              mb: 0.5,
              textTransform: "lowercase",
              color: "#000",
              fontFamily: "Inter, Arial, sans-serif",
            }}
          >
            be
            <Box component="span" sx={{ color: "#A259FF" }}>
              ɘ
            </Box>
            mz
          </Typography>

          {/* Tagline */}
          <Typography
            variant="body1"
            fontWeight="bold"
            color="#000"
            sx={{ mb: 3 }}
          >
            the out & about chat app
          </Typography>

          {/* Success Icon */}
          <CheckCircle
            sx={{
              fontSize: 80,
              color: "#4caf50",
              mb: 2,
            }}
          />

          {/* Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ mb: 2, color: "#000", textTransform: "lowercase" }}
          >
            payment has been completed!
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: "400px", textTransform: "lowercase" }}
          >
            your subscription has been activated  . you can now enjoy
            all the premium features.
          </Typography>

          {/* Setup Account Button */}
          <Button
            variant="contained"
            size="large"
            onClick={handleSetupAccount}
            sx={{
              backgroundColor: "#9347FF",
              color: "#FFFFFF",
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: "lowercase",
              fontSize: "1rem",
              fontWeight: "bold",
              boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
              "&:hover": {
                backgroundColor: "#8938e9",
                boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
              },
            }}
          >
            complete business setup
          </Button>
        </Box>
      </DialogContent>
    </Dialog>

    <EmailVerificationDialog
      open={openEmailVerify}
      onClose={handleEmailDialogClose}
      onSend={handleSendOtp}
      businessEmail={businessEmail}
      isLoading={isSendingOtp}
    />

    <OtpVerificationDialog
      open={openOtpVerify}
      onClose={handleOtpDialogClose}
      onVerify={handleOtpVerify}
      onResend={handleSendOtp}
      email={businessEmail}
      isVerifying={false}
      isResending={isSendingOtp}
      resendInitialSeconds={60}
      isMaxView={false}
    />
    </>
  );
};

export default PaymentSuccessDialog;

